/**
 * DiceUnlockedModal — the "NEW DICE UNLOCKED" reveal after an offer purchase.
 *
 * Shows ALL granted dice at once in a grid (thumbnail over a base plate, like the
 * dice library), each popping in on a stagger with a confetti burst, then a
 * single continue button. `show()` resolves when the player dismisses it, so the
 * purchase choreography can close the offer popup right after.
 *
 * Body-level singleton with injected CSS, mirroring TutorialRewardModal. Sits at
 * z-index 825 so it renders above the offer popup (z 220).
 */

import { sound, SoundId } from '../../engine/audio/sound';

const STYLE_ID = 'sdl-diceunlocked-styles';
const FALLBACK_THUMB = '/assets/ui/dice-thumb-placeholder.png';
const BASE_PLATE = '/assets/ui/dice-base.png';

export class DiceUnlockedModal {
  public element: HTMLElement;
  private gridEl: HTMLElement;
  private confettiEl: HTMLElement;
  private btn: HTMLButtonElement;
  private resolve: (() => void) | null = null;

  constructor() {
    DiceUnlockedModal.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-diceunlocked-host';
    this.element.innerHTML = `
      <div class="sdl-diceunlocked-card" role="dialog" aria-modal="true">
        <div class="sdl-diceunlocked-confetti" aria-hidden="true"></div>
        <div class="sdl-diceunlocked-heading">NEW DICE UNLOCKED</div>
        <div class="sdl-diceunlocked-grid"></div>
        <button class="sdl-diceunlocked-btn">SWEET</button>
      </div>
    `;
    document.body.appendChild(this.element);
    this.gridEl = this.element.querySelector('.sdl-diceunlocked-grid') as HTMLElement;
    this.confettiEl = this.element.querySelector('.sdl-diceunlocked-confetti') as HTMLElement;
    this.btn = this.element.querySelector('.sdl-diceunlocked-btn') as HTMLButtonElement;
    this.btn.addEventListener('click', () => this.dismiss());
  }

  /** Reveal the granted dice. Resolves when dismissed. */
  show(diceCodes: string[], thumbUrl: (id: string) => string): Promise<void> {
    this.gridEl.innerHTML = diceCodes
      .map(
        (id, i) => `
        <div class="sdl-diceunlocked-cell" style="animation-delay:${80 + i * 90}ms">
          <img class="sdl-diceunlocked-base" src="${BASE_PLATE}" alt="" onerror="this.style.display='none'">
          <img class="sdl-diceunlocked-thumb" src="${thumbUrl(id)}"
               onerror="this.onerror=null;this.src='${FALLBACK_THUMB}'" alt="">
        </div>`,
      )
      .join('');
    this.element.classList.add('visible');
    this.burstConfetti();
    // Explosion sound rides the confetti; the reveal stinger rides the dice pop-in.
    sound.play(SoundId.CONFETTI_POP);
    sound.play(SoundId.DICE_REVEAL);
    return new Promise((resolve) => { this.resolve = resolve; });
  }

  isVisible(): boolean {
    return this.element.classList.contains('visible');
  }

  private dismiss(): void {
    this.element.classList.remove('visible');
    this.confettiEl.innerHTML = '';
    const r = this.resolve;
    this.resolve = null;
    r?.();
  }

  destroy(): void {
    this.element.remove();
  }

  private burstConfetti(): void {
    const colors = ['#FFD700', '#00F5FF', '#FF0080', '#00FF88', '#ffffff'];
    let html = '';
    for (let i = 0; i < 36; i++) {
      const left = Math.round((i / 36) * 100);
      const color = colors[i % colors.length];
      const delay = (i % 6) * 40;
      const dur = 900 + (i % 5) * 160;
      const drift = ((i % 7) - 3) * 14;
      html += `<span style="left:${left}%;background:${color};animation-delay:${delay}ms;animation-duration:${dur}ms;--drift:${drift}px"></span>`;
    }
    this.confettiEl.innerHTML = html;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-diceunlocked-host {
        position: fixed; inset: 0; z-index: 825;
        display: none; align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(4, 4, 8, 0.78);
        backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px);
      }
      .sdl-diceunlocked-host.visible { display: flex; }

      .sdl-diceunlocked-card {
        position: relative; overflow: hidden;
        display: flex; flex-direction: column; align-items: center;
        gap: 18px;
        max-width: min(92vw, 460px);
        padding: 28px 24px 24px;
        background: linear-gradient(180deg, #15151c, #0c0c11);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 20px;
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
        /* fill-mode 'both' => the frame before the animation samples already
         * shows the 0% keyframe (opacity 0), not the base style. Without it,
         * Android WebView paints the card fully visible for one frame, then the
         * 0% keyframe hides it for a frame — a flash-then-blink on every open. */
        animation: sdl-diceunlocked-in 0.32s cubic-bezier(0.34, 1.4, 0.6, 1) both;
      }
      @keyframes sdl-diceunlocked-in {
        from { transform: translateY(24px) scale(0.92); opacity: 0; }
        to   { transform: translateY(0) scale(1); opacity: 1; }
      }

      .sdl-diceunlocked-heading {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 32px; letter-spacing: 2px;
        color: var(--gold, #FFD700);
        text-shadow: 0 0 18px rgba(255, 215, 0, 0.5);
      }

      .sdl-diceunlocked-grid {
        display: flex; flex-wrap: wrap; gap: 10px 14px;
        justify-content: center; max-width: 380px;
      }
      .sdl-diceunlocked-cell {
        position: relative; width: 5rem; height: 5.6rem;
        display: flex; align-items: flex-end; justify-content: center;
        opacity: 0;
        animation: sdl-diceunlocked-pop 0.5s cubic-bezier(0.34, 1.5, 0.5, 1) forwards;
      }
      @keyframes sdl-diceunlocked-pop {
        0%   { opacity: 0; transform: scale(0.4) rotate(-18deg); }
        100% { opacity: 1; transform: scale(1) rotate(0deg); }
      }
      .sdl-diceunlocked-base {
        position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
        width: 5.6rem; height: auto; opacity: 0.9; pointer-events: none;
      }
      .sdl-diceunlocked-thumb {
        position: relative; width: 4.3rem; height: auto; margin-bottom: 0.7rem;
        filter: drop-shadow(2px 2px 3px rgba(0, 0, 0, 0.9));
        pointer-events: none;
      }

      .sdl-diceunlocked-btn {
        margin-top: 4px;
        padding: 12px 40px;
        background: linear-gradient(180deg, #ffe169, #f0b400);
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 999px;
        color: #1a1208;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px; letter-spacing: 1.5px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        box-shadow: 0 6px 20px rgba(240, 180, 0, 0.45);
        transition: transform 0.12s ease, filter 0.15s ease;
      }
      .sdl-diceunlocked-btn:active { transform: scale(0.95); filter: brightness(1.08); }

      .sdl-diceunlocked-confetti { position: absolute; inset: 0; pointer-events: none; }
      .sdl-diceunlocked-confetti span {
        position: absolute; top: -12px;
        width: 8px; height: 12px; border-radius: 2px;
        opacity: 0;
        animation-name: sdl-diceunlocked-fall;
        animation-timing-function: ease-in;
        animation-fill-mode: forwards;
      }
      @keyframes sdl-diceunlocked-fall {
        0%   { opacity: 1; transform: translateY(0) translateX(0) rotate(0deg); }
        100% { opacity: 0; transform: translateY(360px) translateX(var(--drift)) rotate(540deg); }
      }
    `;
    document.head.appendChild(style);
  }
}
